import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
  demandeFinancement,
  demandeRemboursement,
  dispositfFinancementMenu,
  dispositifPaiementMenu,
  itemsFinancement,
  itemsFinancementWithoutDraft,
  itemsPaiement,
  itemsPaiementWithoutDraft,
  menu,
} from '@shared/constants/menu.constant';
import { filter, Subscription } from 'rxjs';
import { NgClass } from '@angular/common';
import { Dispositif } from '@features/referentiel/models/dispositif';
import { MesFinancementSearchFormActions } from '@features/mes-financements/store/search/search.actions';
import { Store } from '@ngrx/store';
import { MesRemboursementsSearchFormActions } from '@features/mes-remboursements/store/search/search.actions';

@Component({
  standalone: true,
  selector: 'app-menu-form',
  templateUrl: './menu-form.component.html',
  styleUrls: ['./menu-form.component.scss'],
  imports: [NgClass, RouterLink, TranslatePipe],
})
export class MenuFormComponent implements OnInit, OnDestroy, OnChanges {
  clickedIcon: any = {
    icon: 'home',
    link: ['/mon-tableau-de-bord'],
    actif: true,
  };

  entiteSubscription!: Subscription;
  selectedItem: any;
  isSubItemClicked: any;
  items: any[] = [];
  isHovered = false;
  hoveredItem: any = null;
  activeItem: any = null;
  private documentClickListener!: () => void;
  currentUrl: string = '';

  @Input() menuDisplay: boolean | null = true;
  @Input() isLoggedIn: boolean = false;
  @Input() url: string | null = '/';

  @Input() set dispositifFinancement(value: any) {
    menu.financement.items[0].items = value?.length
      ? itemsFinancement
      : itemsFinancementWithoutDraft;
    if (value?.length && menu.financement.items.length < 2) {
      const items: any[] = [];
      value.map((x: any) => {
        const dispo = dispositfFinancementMenu[x.code.toLowerCase()];
        dispo.dispositif = x;
        items.push(dispo);
      });
      demandeFinancement.items = items as never[];
      menu.financement.items.push(demandeFinancement);
    }
  }

  @Input() set dispositifRemboursement(value: any) {
    menu.paiement.items[0].items = value?.length ? itemsPaiement : itemsPaiementWithoutDraft;
    if (value?.length && menu.paiement.items.length < 2) {
      const items: any[] = [];
      value.map((x: any) => {
        const dispo = dispositifPaiementMenu[x.code.toLowerCase()];
        dispo.dispositif = x;
        items.push(dispo);
      });
      demandeRemboursement.items = items as never[];
      menu.paiement.items.push(demandeRemboursement);
    }
  }

  @Output() closeMenu: EventEmitter<any> = new EventEmitter();
  @Output() menuClick: EventEmitter<any> = new EventEmitter();
  @Output() subMenuclicked = new EventEmitter<Dispositif>();

  constructor(private router: Router, private renderer: Renderer2, private store: Store) {}

  ngOnInit(): void {
    this.items = [menu.accueil, menu.financement, menu.paiement, menu.aide];

    this.items.forEach((item) => {
      item.actif = false;
      item.isSubItemClicked = false;
    });

    this.items.forEach((item) => {
      item.actif = false;
      item.isSubItemClicked = false;
      item.isHovered = false;
    });
    this.documentClickListener = this.renderer.listen('document', 'click', (event) => {
      if (!this.isClickInsideMenu(event)) {
        this.closeAllSubMenus();
      }
    });

    const initialUrl = this.getSegmentBetweenFirstSlashes(this.router.url);
    if (initialUrl) {
      this.currentUrl = initialUrl;
      this.updateMenuItems(initialUrl);
    } else {
      this.setDefaultStyles();
    }

    this.listenToNavigationEnd();
  }

  ngOnChanges({ dispositifFinancement, dispositifRemboursement }: SimpleChanges): void {
    if (dispositifFinancement || dispositifRemboursement) {
      this.listenToNavigationEnd();
      this.updateMenuItems(this.currentUrl);
    }
  }

  updateMenuItems(url: string) {
    this.items = [menu.accueil, menu.financement, menu.paiement, menu.aide];

    let effectiveUrl = url;
    if (url === '/editiques') {
      const fullUrl = this.router.url;
      if (fullUrl.includes('/dossier/')) {
        effectiveUrl = '/mes-financements';
      } else if (fullUrl.includes('/facture/')) {
        effectiveUrl = '/mes-remboursements';
      }
    }

    for (const mainMenuItem of this.items) {
      mainMenuItem.actif = false;
      mainMenuItem.isSubItemClicked = false;

      if (mainMenuItem.items) {
        for (const subMenuItem of mainMenuItem.items) {
          if (subMenuItem.link && subMenuItem.link.includes(effectiveUrl)) {
            this.updateItemsState(mainMenuItem, subMenuItem);
          } else {
            if (subMenuItem.items) {
              for (const subSubMenuItem of subMenuItem.items) {
                if (subSubMenuItem.link && subSubMenuItem.link.includes(effectiveUrl)) {
                  this.updateItemsState(mainMenuItem, subMenuItem);
                }
              }
            }
          }
        }
      } else {
        this.setDefaultStyles();
      }
    }
  }

  listenToNavigationEnd() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const currentUrl = this.getSegmentBetweenFirstSlashes(this.router.url);
      if (currentUrl) {
        this.currentUrl = currentUrl;
        this.updateMenuItems(currentUrl);
      }
    });
  }

  setDefaultStyles() {
    const homeItem = this.items.find((item) => item.icon === 'home');
    if (homeItem) {
      homeItem.actif = true;
      homeItem.isSubItemClicked = true;
    }
  }

  ngOnDestroy(): void {
    if (this.entiteSubscription) {
      this.entiteSubscription.unsubscribe();
    }

    if (this.documentClickListener) {
      this.documentClickListener();
    }
  }

  private isClickInsideMenu(event: MouseEvent): boolean {
    const menuElement = document.querySelector('.menu');
    return menuElement?.contains(event.target as Node) || false;
  }

  private closeAllSubMenus(): void {
    this.items.forEach((item) => {
      item.actif = false;
      item.isHovered = false;
    });
    this.activeItem = null;
  }

  setClickedIcon(item: any) {
    this.items.map((i) => (i.actif = false));

    if (item.icon === 'home') {
      this.items.map((i) => {
        i.actif = false;
        i.isSubItemClicked = false;
      });

      this.setDefaultStyles();
      this.store.dispatch(MesFinancementSearchFormActions.updateKeepHistory({ value: false }));
      this.store.dispatch(MesRemboursementsSearchFormActions.updateKeepHistory({ value: false }));
    }

    this.clickedIcon = item;
  }

  isIconClicked(item: any): boolean {
    return this.clickedIcon.icon === item.icon;
  }

  menuClicked(event: any) {
    this.menuClick.emit(event);
  }

  menuClosed() {
    this.menuDisplay = false;
    this.closeMenu.emit();
  }

  showSubMenu(item: any) {
    if (this.activeItem === item) {
      item.actif = !item.actif;
    } else {
      if (this.activeItem) {
        this.activeItem.actif = false;
      }
      item.actif = true;
      this.activeItem = item;
    }
  }

  hideSubMenu(item: any) {
    if (this.activeItem !== item) {
      item.actif = false;
    }
    item.isHovered = false;
  }

  onMouseOver(item: any): void {
    this.items.map((item) => (item.isHovered = false));
    item.isHovered = true;
  }

  handleSubItemClick(subSubItem: any, parentItem: any) {
    this.items.forEach((item) => (item.isSubItemClicked = false));
    parentItem.isSubItemClicked = true;
    this.setClickedIcon(parentItem);
    if (subSubItem.dispositif) {
      return this.subMenuclicked.emit(subSubItem);
    }
    if (subSubItem.externalLink) {
      window.open(subSubItem.externalLink, '_blank');
    } else if (subSubItem.link) {
      this.handlePreNavigationSideEffects(subSubItem);
      this.router.navigate(subSubItem.link);
    }
  }

  private handlePreNavigationSideEffects(subSubItem: any) {
    const targetUrl = this.router.createUrlTree(subSubItem.link).toString();
    const currentUrl = this.router.url;
    if (currentUrl === targetUrl) {
      return;
    }

    if (targetUrl.includes('/mes-financements')) {
      this.store.dispatch(MesFinancementSearchFormActions.updateKeepHistory({ value: false }));
    }

    if (targetUrl.includes('/mes-remboursements')) {
      this.store.dispatch(MesRemboursementsSearchFormActions.updateKeepHistory({ value: false }));
    }
  }

  private getSegmentBetweenFirstSlashes(url: string): string | null {
    const segments = url.split('/').filter(Boolean);
    return segments.length > 0 ? '/' + segments[0] : null;
  }

  private updateItemsState(item: any, elem: any): void {
    this.items = this.items.map((menu: any) => {
      const updatedMenu = {
        ...menu,
        isSubItemClicked: menu.label === item.label,
        items: menu.items
          ? menu.items.map((subItem: any) => ({
              ...subItem,
              isSubItemClicked: subItem.title === elem.title,
            }))
          : [],
      };
      return updatedMenu;
    });
  }

  onMouseLeaveWrapper(): void {
    this.items.forEach((item) => (item.isHovered = false));
  }
}
